import { FLOW_RUN_LOGS_RETENTION } from 'new/constants/retentionConstants';
import { POLLING_PRESET_INTERVALS } from 'new/constants/pollingConfig';

/**
 * Pagination Configuration
 */
export const PAGINATION = {
  /** Number of logs to fetch per page/request */
  PER_PAGE: 100,
  /** Initial page number for pagination */
  INITIAL_PAGE: 1,
  /** Minimum page number */
  MIN_PAGE: 1,
} as const;

/**
 * Request Throttling & Debouncing Configuration
 */
export const TIMING = {
  /** Minimum delay between consecutive API requests (in milliseconds) */
  THROTTLE_DELAY: 500,
  /** Debounce delay for filter changes (in milliseconds) */
  FILTER_DEBOUNCE_DELAY: 300,
  /** Auto-refresh interval for logs (in milliseconds) - uses FLOW_LOGS preset (10s) */
  AUTO_REFRESH_INTERVAL: POLLING_PRESET_INTERVALS.FLOW_LOGS,
  /** Minimum delay for showing initial loader (in milliseconds) */
  INITIAL_LOADER_MIN_DELAY: 500,
  /** Polling interval for scanning mode (in milliseconds) - uses SCANNING preset (5s) */
  SCANNING_POLL_INTERVAL: POLLING_PRESET_INTERVALS.SCANNING,
} as const;

export const TIME_RANGES = {
  FIVE_MINUTES: 5 * 60 * 1000,
  FIFTEEN_MINUTES: 15 * 60 * 1000,
  THIRTY_MINUTES: 30 * 60 * 1000,
  ONE_HOUR: 3600 * 1000,
  THREE_HOURS: 3 * 3600 * 1000,
  SIX_HOURS: 6 * 3600 * 1000,
  TWELVE_HOURS: 12 * 3600 * 1000,
  TWENTY_FOUR_HOURS: 24 * 3600 * 1000,
  TWO_DAYS: 2 * 24 * 3600 * 1000,
  THREE_DAYS: 3 * 24 * 3600 * 1000,
  THIRTY_DAYS_AGO: new Date().getTime() - FLOW_RUN_LOGS_RETENTION.MILLISECONDS,
} as const;

/**
 * UI Display Configuration
 */
export const DISPLAY = {
  LINE_NUMBER_CHAR_WIDTH: 7,
  MIN_LINE_NUMBER_DIGITS: 3,
  SKELETON_LINES_COUNT: 100,
  MAX_CONSECUTIVE_ERRORS: 2,
} as const;

/**
 * Virtualized List Configuration
 */
export const VIRTUALIZED_LIST = {
  /** Initial firstItemIndex for reverse infinite scroll in LogList */
  INITIAL_FIRST_ITEM_INDEX: 10000000,
  /** Scroll inactivity threshold (in milliseconds) - time before scroll is considered stopped */
  SCROLL_INACTIVITY_THRESHOLD: 500,
  /** Viewport increase value for virtualization (in pixels) - 800px */
  VIEWPORT_INCREASE: 800,
  /** Minimum number of items required before allowing endReached to fire
   * Prevents endReached from firing when logs are cleared after filter changes */
  MIN_ITEMS_FOR_LOAD_MORE: 10,
} as const;

/**
 * Error Messages Configuration
 */
export const ERROR_MESSAGES = {
  FETCH_FAILED: `Failed to load logs. Please try again. Note: Logs are only retained for ${FLOW_RUN_LOGS_RETENTION.DISPLAY_UNIT}.`,
  NO_LOGS_FOR_RUN: 'No logs could be found for this flow.',
  NO_LOGS_FOR_TIME_RANGE: 'No logs could be found for the selected time range.',
  NO_LOGS_FOR_FILTER: 'No logs could be found for this filter.',
  REACHED_END: 'End of results',
} as const;

/**
 * Scanning Mode Specific Messages
 */
export const SCANNING_MESSAGES = {
  INITIAL: '',
  ERROR_PATTERNS: [
    'Source folder did not contain any files that qualified for ingestion',
    'Failed to detect a Nexset',
  ],
  SUCCESS_PATTERN: 'successfully',
  FAILURE_PATTERN: 'Failed to detect a Nexset',
  ERROR_LOG_PATTERN: 'error trying to fetch logs',
} as const;

/**
 * Sort Orders
 * NEWEST = 'desc' (newest logs first)
 * OLDEST = 'asc' (oldest logs first)
 */
export const SORT_ORDER = {
  NEWEST: 'desc' as const,
  OLDEST: 'asc' as const,
} as const;

/**
 * Type exports for better TypeScript support
 */
export type SortOrder = (typeof SORT_ORDER)[keyof typeof SORT_ORDER];
